import { ActionTree } from 'vuex';
import { EFolderAction, EFolderMutation, IFoldersState } from './folders.types';
import { getDefaultState } from './folders.constants';
import { RootState } from '@/store/store';
import { FolderService } from '@/api/folders/services/folder.service';
import { IFolder } from '@/folders/types/folder';
import { ContractManagementService } from '@/api/contract-management/services/contract-management.service';
import { translate } from '@/helpers/translation.helper';
import { alertNamespace, EAlertAction } from '@/alert/store/alert.constants';

export const actions: ActionTree<IFoldersState, RootState> = {
  async [EFolderAction.Load]({ commit, dispatch }, folderId: string) {
    commit(EFolderMutation.SetLoading, true);
    commit(EFolderMutation.SetLoaded, false);
    try {
      const folders: IFolder[] = await FolderService.loadFolders(folderId);
      const breadcrumbs: IFolder[] = await FolderService.loadBreadcrumbs(folderId);
      dispatch(EFolderAction.LoadSucces, { folders, breadcrumbs });
    } catch (e) {
      dispatch(EFolderAction.LoadFailed);
    }
  },
  [EFolderAction.LoadSucces](
    { commit },
    { folders, breadcrumbs }: { folders: IFolder[]; breadcrumbs: IFolder[] }
  ) {
    commit(EFolderMutation.SetFolders, folders);

    const rootBreadcrumb: IFolder = { id: null, name: 'Start' };
    commit(EFolderMutation.SetBreadcrumbs, [rootBreadcrumb, ...breadcrumbs]);

    commit(EFolderMutation.SetLoading, false);
    commit(EFolderMutation.SetLoaded, true);
  },
  [EFolderAction.LoadFailed]({ commit, dispatch }) {
    commit(EFolderMutation.SetLoading, false);
    commit(EFolderMutation.SetLoaded, false);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('could-not-load-documents'),
      { root: true }
    );
  },

  async [EFolderAction.Create](
    { dispatch },
    { name, parentId }: { name: string; parentId?: string }
  ) {
    try {
      const folder: IFolder = await ContractManagementService.createFolder(name, parentId);
      dispatch(EFolderAction.CreateSuccess, folder);
    } catch (e) {
      dispatch(
        `${alertNamespace}/${EAlertAction.ShowDanger}`,
        translate('could-not-create-folder'),
        { root: true }
      );
    }
  },
  [EFolderAction.CreateSuccess]({ commit, state }, folder: IFolder) {
    commit(EFolderMutation.SetFolders, [...state.folders, folder]);
  },

  async [EFolderAction.Update]({ dispatch }, { name, id }: { name: string; id: string }) {
    try {
      const updatedFolder = await ContractManagementService.updateFolderName(id, name);
      dispatch(EFolderAction.UpdateSuccess, updatedFolder);
    } catch (error) {
      dispatch(
        `${alertNamespace}/${EAlertAction.ShowDanger}`,
        translate('could-not-update-folder'),
        { root: true }
      );
    }
  },
  [EFolderAction.UpdateSuccess]({ commit, state }, folder: IFolder) {
    const updatedFolders: IFolder[] = state.folders.map((stateFolder: IFolder) =>
      stateFolder.id === folder.id ? folder : stateFolder
    );
    commit(EFolderMutation.SetFolders, updatedFolders);
  },

  async [EFolderAction.Delete]({ dispatch }, id: string) {
    try {
      await ContractManagementService.deleteFolder(id);
      dispatch(EFolderAction.DeleteSuccess, id);
    } catch (error) {
      dispatch(
        `${alertNamespace}/${EAlertAction.ShowDanger}`,
        translate('could-not-delete-folder'),
        {
          root: true,
        }
      );
    }
  },
  [EFolderAction.DeleteSuccess]({ commit, state }, id: string) {
    const updatedFolders: IFolder[] = state.folders.filter(
      (stateFolder: IFolder) => stateFolder.id !== id
    );
    commit(EFolderMutation.SetFolders, updatedFolders);
  },

  [EFolderAction.Reset]({ commit }) {
    commit(EFolderMutation.SetState, getDefaultState());
  },
};
